import nuskin from './namespace.js';
import './event.js';
import mktRegionMapping from './regionMapping.js';
import RunConfigService from './runConfigService.js';
import axios from 'axios';
import UrlService from './urlService.js';
import storage from './storage.js';
import ShoppingContext from './shoppingContext.js';
import * as _ from 'lodash';

/**
 *
 * BrowserDetection object is immediately executed when the page loads.
 * This Object is used to detect the browser and version the user is using.
 *
 * @namespace ConfigService
 */
let DEFAULT_CONFIG_URI = '/content/configuration/global/markets',
    CONTEXT_CONFIG_URI = '/content/configuration/context/{context}/{region}.service.{country}.json';

let config = {},
    defaultConfig = null,
    lastUrl = '',
    lastContext = '';

let clearConfig = function () {
    config = {};
    lastUrl = '';
    lastContext = '';
};

const setRuntimeOverrides = () => {
    let shoppingContext = ShoppingContext.getShoppingContext();
    if (shoppingContext && shoppingContext.overrides) {
        nuskin.configuration = _.merge(nuskin.configuration, shoppingContext.overrides);
    }
    $.setValue(nuskin.events.global.CONFIGURATION_SET);
    return nuskin.configuration;
};

let getConfiguration = (url, country, domain, forceLoad = false, ignoreContext) => {
    let shoppingContext = ignoreContext ? null : ShoppingContext.getShoppingContext();
    let needToOverride = !!(shoppingContext && shoppingContext.context && country);
    /**
         * If this function is called again with the same url, it will just return the same configuration it loaded
         * previously. So in general, the ajax call will happen again if you change environments or countries.
         */
    if (forceLoad || url !== lastUrl || (shoppingContext && lastContext !== shoppingContext.context)) {
        // TODO: the synchronous approach below works for now but we really ought to return a promise and resolve upstream.
        // return await fetch(url, {
        //     method: 'GET'
        // })
        //     .then(res => res.json())
        //     .then((res) => {
        //         config = res;
        //         nuskin.configuration = config;
        //         lastUrl = url;
        //         $.setValue(nuskin.events.global.CONFIGURATION_SET);
        //     })
        //     .catch(xhr => {
        //         nuskin.configuration = null;
        //         config = {error: 'Ajax error in getting configuration: ' + url + ' with xhr: ' + JSON.stringify(xhr)};
        //         console.log("could not load market configuration! error:" + xhr);
        //         console.log("Configuration URL: " + url);
        //     });
        $.ajax({
            type: "GET",
            url: url,
            dataType: "json",
            async: false
        }).done((data) => {
            config = doConversions(data);
            nuskin.configuration = config;
            lastUrl = url;
        }).fail((xhr) => {
            // if config has not been loaded yet then return an error
            //  otherwise use previously cached config
            if (!config.siteUrl) {
                nuskin.configuration = null;
                config = {error: 'Ajax error in getting configuration: ' + url + ' with xhr: ' + JSON.stringify(xhr)};
                console.log("could not load market configuration! error:" + xhr);
                console.log("Configuration URL: " + url);
            }
            // to have the code try again the next time marketConfig is requested
            lastUrl = '';
        });

        // Override the config if we need to
        if (needToOverride) {
            let contextUrl = domain + CONTEXT_CONFIG_URI.replace('{context}', shoppingContext.context).replace('{region}', mktRegionMapping[country]).replace('{country}', country);
            $.ajax({
                type: "GET",
                url: contextUrl,
                dataType: "json",
                async: false
            }).done((data) => {
                nuskin.configuration = config = _.merge(nuskin.configuration, data);
            }).fail((xhr) => {
                console.log("Failed to get override config: " + xhr);
            });
        }
    }
    // Always set runtime overrides
    // If we get away from synchronous config calls then we need to wait for the promises to finish before this next step
    if (needToOverride) {
        config = setRuntimeOverrides();
        lastContext = shoppingContext.context;
    }
    //This works here because the configuration ajax calls are synchronous.  If we ever switch to async then we need to publish after the ajax call comes back
    $.setValue(nuskin.events.global.CONFIGURATION_SET);
    return config;
};

/**
     * This will add custom script and style tags in the context config to the page specified
     * You need to add the following configs to your context config to your page
     *
     * custom${pageName}ScriptUrl
     * custom${pageName}Script
     * custom${pageName}StyleUrl
     * custom${pageName}Styles
     *
     *  @param {string} pageName  Required
     */
let loadCustomJSStylesOnPage = (pageName) => {
    if (nuskin.configuration.hasOwnProperty(`custom${pageName}ScriptUrl`)) {
        let scriptEle = document.createElement('script');
        scriptEle.type = 'text/javascript';
        scriptEle.setAttribute('src', nuskin.configuration[`custom${pageName}ScriptUrl`]);
        document.head.appendChild(scriptEle);
    }
    if (nuskin.configuration.hasOwnProperty(`custom${pageName}Script`)) {
        // Run the custom script
        eval(nuskin.configuration[`custom${pageName}Script`]);
    }

    if (nuskin.configuration.hasOwnProperty(`custom${pageName}StyleUrl`)) {
        let styleEle = document.createElement('style');
        styleEle.type = 'text/css';
        styleEle.setAttribute('src', nuskin.configuration[`custom${pageName}StyleUrl`]);
        document.head.appendChild(styleEle);
    }
    if (nuskin.configuration.hasOwnProperty(`custom${pageName}Styles`)) {
        let styleEle = document.createElement('style');
        styleEle.type = 'text/css';
        styleEle.appendChild(document.createTextNode(nuskin.configuration[`custom${pageName}Styles`]));
        document.head.appendChild(styleEle);
    }
};

/**
 *
 * @param {*} forceLoad
 * @param {*} _country
 * @param {Boolean} ignoreContext - used by salesEventService to avoid shopping context loss (multi-tab issue)
 */
let getMarketConfig = function (forceLoad, _country, ignoreContext) {
    let country = _country,
        runConfig = RunConfigService.getRunConfig();

    if (!country) {
        country = runConfig.country;
    }

    if (!country || typeof(country) === "undefined") {
        country = "US";
    }

    // if (forceLoad) {
    //     clearConfig();
    // }

    let configUri;
    if (typeof(runConfig) !== undefined && runConfig.siteConfigurationPath !== undefined && runConfig.country === country) {
        configUri = runConfig.siteConfigurationPath;
    } else {
        runConfig.country = country;
        if (runConfig.assessmentApp) {
            // beginning slash gets stripped off during the agelocme build process.
            configUri = '/' + DEFAULT_CONFIG_URI + '/' + mktRegionMapping[country];
        } else {
            configUri = DEFAULT_CONFIG_URI + '/' + mktRegionMapping[country];
        }
        runConfig.siteConfigurationPath = configUri;
    }

    let configUrl,
        fixedBaseUrl;

    if (runConfig.assessmentApp) {
        if (!config.siteUrl) {
            if (defaultConfig) {
                config = defaultConfig;
            } else {
                // Get default config first
                let url = 'content/configuration/global/markets/' + mktRegionMapping[country] + '.service.' + country + '.json'
                getConfiguration(url, null, null, false, ignoreContext);
                if (config.siteUrl) {
                    defaultConfig = JSON.parse(JSON.stringify(config));
                }
            }
        }
        fixedBaseUrl = config.siteUrl;
    } else {
        fixedBaseUrl = runConfig.fbBaseUrl ? runConfig.fbBaseUrl : runConfig.baseUrl;
    }

    if (!fixedBaseUrl) {
        fixedBaseUrl = "";
    }
    if (fixedBaseUrl && (fixedBaseUrl.length > 0) && (fixedBaseUrl.charAt(fixedBaseUrl.length - 1) === '/')) {
        fixedBaseUrl = fixedBaseUrl.slice(0, fixedBaseUrl.length - 1);
    }

    configUrl = fixedBaseUrl + configUri + '.service.' + country + '.json';
    getConfiguration(configUrl, country, fixedBaseUrl, forceLoad, ignoreContext);
    if (config.error && defaultConfig) {
        config = nuskin.configuration = defaultConfig;
    }

    if (runConfig.assessmentApp) {
        overwriteAgelocConfig();
    }

    // Look to see if there is a storage value that overwrites config
    overrideConfigWithStorage(country);

    return config;
};

let getAgelocConfig = function (_market) {
    return getMarketConfig(true, _market);
};

let getUrlConfig = function (url, clearIt) {
    if (clearIt) {
        clearConfig();
    }

    return getConfiguration(url);
};

let setConfig = function (configObj) {
    config = configObj;

    return config;
};

function doConversions(_config) {
    let config = convertSkuMaxQty(_config);

    return config;
}

function convertSkuMaxQty(_config) {
    // skuMaxQty adjustment...
    // convert sku maxQty from array of strings to json object
    // from [{'sku':'00000000', 'maxQty':2},
    //       {'sku':'00000001', 'maxQty':3},
    //       {'sku':'00000002', 'maxQty':4}]
    // to {'00000000':2, '00000001':3, '00000002':4}
    if (_config.skuMaxQty) {
        let parsed = {},
            toParse = _config.skuMaxQty instanceof Array ? _config.skuMaxQty : [_config.skuMaxQty];

        toParse.forEach(item => {
            try {
                let itemJson = JSON.parse(item),
                    sku = itemJson.sku.length === 8 ? itemJson.sku : null,
                    maxQty = parseInt(itemJson.maxQty);

                if (sku && maxQty > 0) {
                    parsed[sku] = maxQty;
                }
            } catch (e) {/* skip the element */}
        });

        _config.skuMaxQty = parsed;
    }

    return _config;
}

function overwriteAgelocConfig() {
    if (config.hasOwnProperty("agelocme")) {
        // disable gigyaLogin in the app right now.  There are issues with building and iOS and stuff
        config.agelocme.override_useGigyaLogin = false;

        let agelocme = config.agelocme;
        for (let agelocProp in agelocme) {
            if (agelocme.hasOwnProperty(agelocProp)) {
                if (agelocProp.match(/^override_/g) && agelocme[agelocProp] !== "inherit" && agelocme[agelocProp] !== "") { //override and not empty
                    let replaceProp = agelocProp.substring(9);                                                              //Strip off override_

                    // TODO: Temporary fix for AEM-11189
                    if (agelocProp === 'override_showShippingPromoCode' ||
                            agelocProp === 'override_showPaymentPromoCodeOffers' ||
                            agelocProp === 'override_showProductPromoCode') {

                        config.checkout[replaceProp] = agelocme[agelocProp];
                    } else {
                        replaceProperty(replaceProp, agelocme[agelocProp]);
                    }
                }
            }
        }
    }
}

function overrideConfigWithStorage(country) {
    let storageObject = storage.metadata.CONFIGURATION_OVERRIDE
    storageObject.key = storage.keys.CONFIGURATION_OVERRIDE + '_' + country
    let overrideValues = storage.getItem(storageObject)
    if (overrideValues) {
        config = _.merge(config, overrideValues)
    }
}

/*
    *   This is a function that is currently only invoked using the console, but it allows a tester to override values in the config service.
    *   Here is an example of how to use it in the console:
    *
    *   aem.ConfigService.setConfigOverride('PH', {loyalty:{enabled:true}})
    *
    *   This function would set the loyalty enabled value to true in the Philippines market
    */

let setConfigOverride = (country, values) => {
    let storageObject = storage.metadata.CONFIGURATION_OVERRIDE
    storageObject.key = storage.keys.CONFIGURATION_OVERRIDE + '_' + country
    try {
        storage.setItem(storageObject, values)
    } catch (err) {
        console.log(err)
    }
}

function replaceProperty(replaceProp, replaceValue) {
    if (config.hasOwnProperty(replaceProp)) {
        config[replaceProp] = replaceValue;
    } else {
        for (let property in config) {
            if (config.hasOwnProperty(property)) {
                if (property !== "agelocme" && config[property].hasOwnProperty(replaceProp)) {
                    config[property][replaceProp] = replaceValue;
                }
            }
        }
    }
}

export default {
    getMarketConfig: getMarketConfig,
    loadCustomJSStylesOnPage: loadCustomJSStylesOnPage,
    getAgelocConfig: getAgelocConfig,
    getUrlConfig: getUrlConfig,
    setConfig: setConfig,
    setConfigOverride: setConfigOverride
}
